// Updated full code with loading indicator for all async actions

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router-dom";
import BPUserDetailsComponent from "./component";

const BPUserDetailsContainer = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [BPList, setBPList] = useState([]);
  const [filteredBPList, setFilteredBPList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedDepartmentName, setEditedDepartmentName] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("active");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleGetCorporateSeries();
  }, []);

  useEffect(() => {
    filterBPList();
  }, [BPList, searchValue, currentStatusFilter]);

  const handleGetCorporateSeries = (offsetValue = 0, append = false) => {
    if (loading) return;
    setLoading(true);

    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}?limit=50&offset=${offsetValue}`,
      "GET",
      undefined,
      (response) => {
        const newList = response || [];
        if (append) {
          setBPList((prev) => [...prev, ...newList]);
        } else {
          setBPList(newList);
        }
        if (newList.length < 50) setHasMore(false);
        setFilteredBPList((prev) => (append ? [...prev, ...newList] : newList));
        setOffset(offsetValue);
        setLoading(false);
      },
      (err) => {
        toastr.error("Failed", "Unable to fetch business profiles.");
        setLoading(false);
      }
    );
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <=
      e.target.clientHeight + 50;
    if (bottom && hasMore && !loading) {
      const nextOffset = offset + 1;
      handleGetCorporateSeries(nextOffset, true);
    }
  };

  const getSelectedEmployeeIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.usersCorporateId)
      .filter(Boolean);
  };

  const handleBulkActivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No employee IDs found for selected rows.");
      return;
    }
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}/activate`,
      "POST",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", "Users activated successfully.");
        handleGetCorporateSeries();
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  const handleBulkDeactivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No employee IDs found for selected rows.");
      return;
    }
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}`,
      "DELETE",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", "Users deactivated successfully.");
        handleGetCorporateSeries();
        setSelectedRows([]);
        setLoading(false);
      },
      () => {
        toastr.error("Failed", "Unable to deactivate selected users.");
        setLoading(false);
      }
    );
  };

  const handleUpdateUser = (ucmId) => {
    if (!ucmId) {
      toastr.error("Error", "No employee ID provided");
      return;
    }
    const userToUpdate = filteredBPList[currentRowIndex];
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${ucmId}`,
      "PATCH",
      {
        corporateId: id,
        userName: editedName,
        email: userToUpdate.emailId,
        businessName: editedDepartmentName
      },
      () => {
        toastr.success("Success", "User updated successfully");
        handleGetCorporateSeries();
        setEditOpen(false);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };

  const filterBPList = () => {
    let filtered = [...BPList];
    if (currentStatusFilter === "active") {
      filtered = filtered.filter((user) => user.status === true);
    } else if (currentStatusFilter === "inactive") {
      filtered = filtered.filter((user) => user.status === false);
    }
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.ucmId?.toString().toLowerCase().includes(searchLower) ||
          user.userName?.toLowerCase().includes(searchLower)
      );
    }
    setFilteredBPList(filtered);
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);
  const handleStatusToggle = (status) => {
    setCurrentStatusFilter(status);
    setSelectedRows([]);
  };
  const handleSelectAll = (checked) => {
    setSelectedRows(checked ? filteredBPList.map((_, i) => i) : []);
  };
  const handleCheckboxChange = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };
  const handleEditClick = (index) => {
    setCurrentRowIndex(index);
    const user = filteredBPList[index];
    setEditedName(user.userName);
    setEditedDepartmentName(user.businessName || '');
    setEditOpen(true);
  };
  const handleSaveEdit = () => {
    if (currentRowIndex === null) return;
    const userToUpdate = filteredBPList[currentRowIndex];
    handleUpdateUser(userToUpdate.ucmId);
  };
  const handleUnlinkClick = (index) => {
    setCurrentRowIndex(index);
    setUnlinkOpen(true);
  };
  const handleConfirmUnlink = () => {
    const userToUnlink = filteredBPList[currentRowIndex];
    const userUcmId = userToUnlink?.ucmId;
    if (!userUcmId) {
      toastr.error("Error", "No employee ID found to unlink.");
      return;
    }
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/approve/${userUcmId}`,
      "POST",
      undefined,
      () => {
        toastr.success("Success", "User unlinked successfully.");
        handleGetCorporateSeries();
        setUnlinkOpen(false);
        setLoading(false);
      },
      (err) => {
        const errorMsg = err?.status?.message || "Something went wrong.";
        toastr.error("Error", errorMsg);
        setLoading(false);
      }
    );
  };
  const handleAddNewUser = () => {
    navigate(`/home/customermanager/bpmanagementlist/bpmanagementusercreation/${id}`);
  };

  return (
    <BPUserDetailsComponent
      BPList={filteredBPList}
      selectedRows={selectedRows}
      handleSelectAll={handleSelectAll}
      handleCheckboxChange={handleCheckboxChange}
      editOpen={editOpen}
      setEditOpen={setEditOpen}
      editedName={editedName}
      setEditedName={setEditedName}
      editedDepartmentName={editedDepartmentName}
      setEditedDepartmentName={setEditedDepartmentName}
      handleEditClick={handleEditClick}
      handleSaveEdit={handleSaveEdit}
      unlinkOpen={unlinkOpen}
      setUnlinkOpen={setUnlinkOpen}
      handleUnlinkClick={handleUnlinkClick}
      handleConfirmUnlink={handleConfirmUnlink}
      searchValue={searchValue}
      handleSearchChange={handleSearchChange}
      handleStatusToggle={handleStatusToggle}
      currentStatusFilter={currentStatusFilter}
      handleAddNewUser={handleAddNewUser}
      handleBulkActivate={handleBulkActivate}
      handleBulkDeactivate={handleBulkDeactivate}
      handleScroll={handleScroll}
      loading={loading}
    />
  );
};

export default connect(null, { getDataFromAPI })(BPUserDetailsContainer);
